/*
 * HistoryPane — past conversations, on the left.
 *
 * Conversations live in this browser, not on the appliance: the edge keeps one
 * rolling message window per conversation ID and reaps it after a couple of
 * hours, which is the wrong lifetime for "what did I ask last week". Keeping
 * the list in localStorage, keyed per node, means history survives a restart of
 * the edge — and an edge restart is routine here.
 *
 * Only the transcript is stored. Proposals and drawings stay on the appliance,
 * because replaying a stale proposal against changed state is exactly the thing
 * the proposal TTL exists to prevent.
 */

import { useState, useEffect, useCallback } from "react";

const KEY = "nabx.ada.history.v2";
const ACTIVE_KEY = "nabx.ada.active.v2";

/* Superseded stores. Read once on load and deleted — a version bump that only
   orphans the old data leaves it sitting in the browser forever, which is the
   wrong answer when the reason for the bump was "erase it". */
const RETIRED = ["nabx.ada.history.v1", "nabx.ada.active.v1"];

(() => {
  try {
    RETIRED.forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
  } catch {
    /* private mode — nothing was stored to begin with */
  }
})();
const MAX_CONVERSATIONS = 40;

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "{}");
  } catch {
    return {};
  }
};

const save = (all) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* quota or private mode — history is a convenience, never a dependency */
  }
};

/** useHistory manages the stored conversations for one node. */
export function useHistory(nodeId) {
  const [items, setItems] = useState([]);

  const refresh = useCallback(() => {
    const all = load();
    const forNode = all[nodeId || "_"] || [];
    setItems(forNode);
  }, [nodeId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Title comes from the first question asked — the same thing the operator
  // would call it themselves.
  //
  // Re-opening a conversation replays its messages into state, which lands here
  // looking exactly like a save. Treated as one, every conversation jumped to
  // the top of the list stamped "just now" the moment it was READ, which is not
  // what the list is for: it orders by when you last SAID something. So an
  // unchanged transcript is left alone entirely — same time, same position.
  const remember = useCallback((conversationId, messages) => {
    if (!messages.length) return;
    const firstUser = messages.find((m) => m.role === "user");
    if (!firstUser) return;

    const all = load();
    const key = nodeId || "_";
    const list = all[key] || [];
    const existing = list.find((c) => c.id === conversationId);
    if (existing && unchanged(existing.messages, messages)) return;

    const rest = list.filter((c) => c.id !== conversationId);
    rest.unshift({
      id: conversationId,
      title: firstUser.text.slice(0, 80),
      at: Date.now(),
      messages,
    });
    all[key] = rest.slice(0, MAX_CONVERSATIONS);
    save(all);
    setItems(all[key]);
  }, [nodeId]);

  const remove = useCallback((conversationId) => {
    const all = load();
    const key = nodeId || "_";
    all[key] = (all[key] || []).filter((c) => c.id !== conversationId);
    save(all);
    setItems(all[key]);
  }, [nodeId]);

  const clear = useCallback(() => {
    const all = load();
    delete all[nodeId || "_"];
    save(all);
    setItems([]);
  }, [nodeId]);

  return { items, remember, remove, clear, refresh };
}

/* Cheap enough to run on every settle, and exact where it matters: a reply
   appended to the end changes the count, and an edit changes the last entry. */
function unchanged(a, b) {
  if (!Array.isArray(a) || a.length !== b.length) return false;
  if (!a.length) return true;
  const x = a[a.length - 1];
  const y = b[b.length - 1];
  return x.role === y.role && x.text === y.text && x.name === y.name;
}

/**
 * loadConversation reads one stored conversation directly.
 *
 * The window needs this before the hook's state has settled — on mount it has
 * to know whether it is resuming something, and the answer cannot wait a render.
 */
export function loadConversation(nodeId, conversationId) {
  if (!conversationId) return null;
  const all = load();
  return (all[nodeId || "_"] || []).find((c) => c.id === conversationId) || null;
}

/* Which conversation this tab was last looking at, per node. Session-scoped on
   purpose: it answers "where was I", which belongs to this sitting, while the
   conversations themselves outlive it. */
export function readActive(nodeId) {
  try {
    return JSON.parse(sessionStorage.getItem(ACTIVE_KEY) || "{}")[nodeId || "_"] || "";
  } catch {
    return "";
  }
}

export function writeActive(nodeId, conversationId) {
  try {
    const all = JSON.parse(sessionStorage.getItem(ACTIVE_KEY) || "{}");
    all[nodeId || "_"] = conversationId || "";
    sessionStorage.setItem(ACTIVE_KEY, JSON.stringify(all));
  } catch {
    /* private mode — resuming is a convenience, never a dependency */
  }
}

export function relativeTime(ms) {
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ms).toLocaleDateString();
}
